import { makeAutoObservable, observable, runInAction } from "mobx";
import type { TProgramsListLayout } from "@/types/project";

const STORAGE_KEY = "programs-list-view-preferences";

/**
 * Cross-session persistence for the workspace Programs list view (Table /
 * Board / Timeline).
 *
 * Mirrors {@link ProjectsListViewPreferencesStore}: the in-page source of truth
 * stays the URL (``?layout=…``); this store only remembers a user's last-used
 * layout so a fresh visit with no layout in the URL (sidebar navigation, a
 * bookmark, a new session) can restore it. ``localStorage`` is the right
 * backend — a last-used layout is a client-only UI preference, not server
 * state — and keeping the read/write in a store (rather than poking
 * ``localStorage`` from the component) gives us SSR-safety + error handling in
 * one place.
 *
 * Keyed by workspace slug: the Programs list is workspace-scoped.
 */
export class ProgramsListViewPreferencesStore {
  layoutByWorkspace = observable.map<string, TProgramsListLayout>();

  constructor() {
    makeAutoObservable(this, {} as never, { autoBind: true });
    this.hydrate();
  }

  private hydrate(): void {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        layoutByWorkspace?: Record<string, TProgramsListLayout>;
      };
      runInAction(() => {
        this.layoutByWorkspace.replace(parsed.layoutByWorkspace ?? {});
      });
    } catch (err) {
      // Corrupt or unreadable (private mode / quota) — start clean.
      console.warn("[programs-list-prefs] hydrate failed; resetting", err);
      runInAction(() => {
        this.layoutByWorkspace.clear();
      });
    }
  }

  private persist(): void {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ layoutByWorkspace: Object.fromEntries(this.layoutByWorkspace) }),
      );
    } catch {
      // Best-effort: localStorage can throw in private mode / on quota.
    }
  }

  getLayout(workspaceSlug: string): TProgramsListLayout | null {
    return this.layoutByWorkspace.get(workspaceSlug) ?? null;
  }

  setLayout(workspaceSlug: string, layout: TProgramsListLayout): void {
    if (this.layoutByWorkspace.get(workspaceSlug) === layout) return;
    this.layoutByWorkspace.set(workspaceSlug, layout);
    this.persist();
  }
}
